import { Tag } from './model';

/**
 * Canonical allowed characters for tag validation.
 * Sourced from ViewTagsComponent.allowedChars.
 */
export const TAG_ALLOWED_CHARS = " ~&+,\\[\\]':;=?@#{}|<>.^*()%!_-";

/**
 * Result of parsing a tag string.
 */
export interface TagParseResult {
  valid: string[];
  invalid: string[];
}

/**
 * Validates a single token against allowed characters.
 * Case-sensitive: preserves original case.
 *
 * @param token - token to validate (should be trimmed)
 * @param allowedChars - string of allowed special characters
 * @returns true if token contains only alphanumeric + allowed chars
 */
export function validateToken(token: string, allowedChars: string = TAG_ALLOWED_CHARS): boolean {
  if (!token || token.length === 0) {
    return false;
  }

  // Build regex: alphanumeric + allowed special chars
  // Escape special regex chars in allowedChars (already escaped in constant)
  const regexPattern = `^[a-zA-Z0-9${allowedChars}]+$`;
  const regex = new RegExp(regexPattern);
  return regex.test(token);
}

/**
 * Formats an array of tags as a quoted comma-separated string.
 *
 * @param tags - array of Tag objects
 * @returns quoted CSV string (e.g., "Tag1","Tag2","Tag3")
 */
export function formatTagsForClipboard(tags: Tag[]): string {
  if (!tags || tags.length === 0) {
    return '';
  }

  return tags.map(tag => `"${tag.name}"`).join(',');
}

/**
 * Deduplicates tag names against a list of existing tags (case-sensitive, exact match).
 *
 * @param tokenNames - array of token strings to check
 * @param existingTags - array of existing Tag objects to compare against
 * @returns array of token names that do not already exist (case-sensitive, exact match)
 */
export function deduplicateTagNames(tokenNames: string[], existingTags: Tag[]): string[] {
  const existingNames = new Set(existingTags.map(tag => tag.name));
  return tokenNames.filter(name => !existingNames.has(name));
}
